import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { EmbedLog, EmbedLogWithContent, hiddenResourceKeysByEntity, LogContent } from '@data/models/audit.model';

interface AuditLogValue {
    raw: string;
    parsed: unknown;
}

interface AuditDiffLog {
    path: string;
    hasTooltip: boolean;
    isPreviousCurrentDiff: boolean;
    isAddedRemovedDiff: boolean;
    isObject: boolean;
    current: AuditLogValue[];
    previous: AuditLogValue[];
    added: AuditLogValue[];
    removed: AuditLogValue[];
}

const ANIMATION_DELAY = 150;

@Component({
    selector: 'syn-audit-diff',
    templateUrl: './audit-diff.component.html',
    styleUrls: ['./audit-diff.component.scss']
})
export class AuditDiffComponent implements OnChanges {

    @Input() public log: EmbedLogWithContent<'$who'|'$previousWho'>;

    @Input() public nextLog: EmbedLog<'$who'> | undefined;

    @Input() public previousLog: EmbedLog<'$who'> | undefined;

    @Input() public loading: boolean;

    @Output() public loadPreviousLog = new EventEmitter<void>();

    @Output() public loadNextLog = new EventEmitter<void>();

    public diffLogs: AuditDiffLog[];

    public animate = true;
    public leftAnimation = false;
    public rightAnimation = false;
    public resource: { key: string; value: string }[];

    public ngOnChanges(): void {
        this.animate = false;
        this.rightAnimation = false;
        this.leftAnimation = false;
        const hiddenResourceKeys = hiddenResourceKeysByEntity[this.log.entity] || [];
        this.resource = Object.entries(this.log.resource)
            .filter(([key]) => !hiddenResourceKeys.includes(key))
            .map(([key, value]) => ({ key, value }));

        this.diffLogs = (this.log.content || []).map(content => {
            const auditLogDiff: Partial<AuditDiffLog> = {
                path: content.path,
                hasTooltip: this.hasTooltip(content),
                isPreviousCurrentDiff: this.isPreviousCurrentDiff(content),
                isAddedRemovedDiff: this.isAddedRemovedDiff(content),
                previous: [],
                current: [],
                added: [],
                removed: [],
            };

            const toAuditLogValue: (raw: string) => AuditLogValue = raw => {
                const parsed = this.parse(raw);

                if (this.isObject(parsed)) {
                    raw = JSON.stringify(parsed, undefined, 2);
                }

                return {
                    raw, parsed
                };
            };

            const isObject = (logValues: AuditLogValue[]) => logValues.some(v => this.isObject(v.parsed));

            if (auditLogDiff.isPreviousCurrentDiff) {
                if (content.current) {
                    const current = toAuditLogValue(content.current);
                    auditLogDiff.current = Array.isArray(current.parsed) ? current.parsed.map(toAuditLogValue) : [current];
                }

                if (content.previous) {
                    const previous = toAuditLogValue(content.previous);
                    auditLogDiff.previous = Array.isArray(previous.parsed) ? previous.parsed.map(toAuditLogValue) : [previous];
                }

                auditLogDiff.isObject = isObject([...auditLogDiff.current, ...auditLogDiff.previous]);
            }

            if (auditLogDiff.isAddedRemovedDiff) {
                auditLogDiff.added = content.added.map(toAuditLogValue);
                auditLogDiff.removed = content.removed.map(toAuditLogValue);
                auditLogDiff.isObject = isObject([...auditLogDiff.added, ...auditLogDiff.removed]);
            }

            return auditLogDiff as AuditDiffLog;
        });
    }

    public goPreviousLog(): void {
        this.animate = true;
        this.leftAnimation = false;
        this.rightAnimation = true;
        setTimeout(() => this.loadPreviousLog.emit(), ANIMATION_DELAY);
    }

    public goNextLog(): void {
        this.animate = true;
        this.leftAnimation = true;
        this.rightAnimation = false;
        setTimeout(() => this.loadNextLog.emit(), ANIMATION_DELAY);
    }

    public isObject(value: unknown): boolean {
        return typeof value === 'object';
    }

    private parse(value: string): unknown {
        try {
            return JSON.parse(value);
        } catch (e) {
            return value;
        }
    }

    private isPreviousCurrentDiff({ previous, current }: LogContent): boolean {
        return previous.length > 0 || current.length > 0;
    }

    private isAddedRemovedDiff({ added, removed}: LogContent): boolean {
        return added.length > 0 || removed.length > 0;
    }

    private hasTooltip(logContent: LogContent): boolean {
        if (this.isPreviousCurrentDiff(logContent)) {
            const { previous, current } = logContent;
            return this.isObject(this.parse(previous)) || this.isObject(this.parse(current));
        } else if (this.isAddedRemovedDiff(logContent)) {
            const { added, removed } = logContent;
            return [...added, ...removed].map(this.parse).some(this.isObject);
        }

        return false;
    }
}
